import React, { useEffect, useRef, useState } from "react";
import { PIN_STATUSES } from "../../../helpers/propertyPins";
import PinGlyph from "../../common/PinGlyph";
import styles from "./Map.module.css";

const SHOW_ZERO_KEY = "jobTrackerShowZeroStatuses";

function readShowZero() {
  try {
    return localStorage.getItem(SHOW_ZERO_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Job tracker pins on the map, in one compact row: a "Jobs" show/hide toggle
 * with the total, a pin and count per status, and a chevron opening the full
 * breakdown (with the choice to list zero-count statuses in the row too) and
 * "Placed by": who placed the pins, to show everyone's or one person's
 * (`placers`, `placedBy` = member id or null, `onPlacedBy`).
 */
export default function PinLegend({ features, visible, onToggle, top, placers = [], placedBy = null, onPlacedBy }) {
  const [open, setOpen] = useState(false);
  const [showZero, setShowZero] = useState(readShowZero);
  const rootRef = useRef(null);

  const counts = {};
  (features || []).forEach((feature) => {
    const key = feature.properties?.status;
    counts[key] = (counts[key] || 0) + 1;
  });
  const total = (features || []).length;
  // Shown even for a single person: it answers "who placed these?" at a glance.
  const showPlacers = placers.length > 0 && typeof onPlacedBy === "function";
  const everyoneCount = placers.reduce((sum, person) => sum + person.count, 0);
  const chosen = placers.find((person) => person.id === placedBy) || null;
  const chips = PIN_STATUSES.filter((status) => showZero || (counts[status.key] || 0) > 0);

  // The menu closes on a click anywhere else (the map included) or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleShowZero = (checked) => {
    setShowZero(checked);
    try {
      localStorage.setItem(SHOW_ZERO_KEY, checked ? "1" : "0");
    } catch {
      // A blocked storage only costs remembering the choice.
    }
  };

  return (
    <div ref={rootRef} className={styles.legend} style={{ top }} data-testid="pin-legend">
      <button
        type="button"
        className={visible ? styles.jobsToggle : `${styles.jobsToggle} ${styles.off}`}
        aria-pressed={visible}
        aria-label="Show job tracker pins"
        onClick={() => onToggle(!visible)}
      >
        <span className={styles.check} aria-hidden="true">
          {visible && (
            <svg width="11" height="11" viewBox="0 0 12 12">
              <path d="M2.5 6.2 5 8.6l4.6-5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span>Jobs</span>
        {chosen && (
          <span className={styles.placedByTag} title={`Placed by ${chosen.label}`} data-testid="pin-placed-by-tag">
            · {chosen.label}
          </span>
        )}
        <span className={styles.total}>{total}</span>
      </button>
      <span className={styles.divider} aria-hidden="true" />
      <div className={styles.chips}>
        {chips.map((status) => (
          <span
            key={status.key}
            className={styles.chip}
            style={{ color: status.ink }}
            title={`${status.label}: ${counts[status.key] || 0}`}
            data-testid={`pin-chip-${status.key}`}
          >
            <PinGlyph color={status.color} height={14} className={styles.chipPin} />
            {counts[status.key] || 0}
          </span>
        ))}
      </div>
      <button
        type="button"
        className={styles.more}
        aria-label="Job tracker status breakdown"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{ transform: open ? "rotate(180deg)" : "none" }}
        >
          <path d="M2 3.5 5 6.5l3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className={styles.menu} data-testid="pin-legend-menu">
          <div className={styles.menuTitle}>Job tracker statuses</div>
          {PIN_STATUSES.map((status) => (
            <div key={status.key} className={styles.menuItem}>
              <PinGlyph color={status.color} height={14} className={styles.chipPin} />
              {status.label}
              <span className={styles.menuCount}>{counts[status.key] || 0}</span>
            </div>
          ))}
          {showPlacers && (
            <div className={styles.placedBy} role="radiogroup" aria-label="Placed by">
              <div className={styles.menuTitle}>Placed by</div>
              {[{ id: null, label: "Everyone", count: everyoneCount }, ...placers].map((person) => (
                <label key={person.id ?? "everyone"} className={styles.menuItem}>
                  <input
                    type="radio"
                    name="jobTrackerPlacedBy"
                    checked={placedBy === person.id}
                    onChange={() => onPlacedBy(person.id)}
                  />
                  {person.label}
                  <span className={styles.menuCount}>{person.count}</span>
                </label>
              ))}
            </div>
          )}
          <label className={styles.switchLine}>
            Show zero-count statuses
            <input
              type="checkbox"
              checked={showZero}
              onChange={(event) => toggleShowZero(event.target.checked)}
            />
          </label>
        </div>
      )}
    </div>
  );
}
